import Button from '@/src/components/atom/Button';
import Dialog from '@/src/components/atom/Dialog';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { RecyclingCouponDto } from '@/src/models/RecyclingCouponDto';
import SectionLabel from '@/src/components/molecule/SectionLabel';
import dayjs from 'dayjs';
import { PutRecyclingCouponRequestBody } from '@/src/apis/rest/recyclingCoupon.dtos';
import { useController, useForm } from 'react-hook-form';
import { Form, FormField } from '@/src/components/ui/form';
import FormContent from '@/src/components/molecule/FormContent';
import Input from '@/src/components/atom/Input';
import { DateRange } from 'react-day-picker';
import DateRangePicker from '@/src/components/atom/DateRangePicker';
import RadioGroup from '@/src/components/atom/RadioGroup';
import ImageUpload from '@/src/components/atom/ImageUpload';
import { Media } from '@/src/models/MeidaDto';
import IconButton from '@/src/components/atom/Button/IconButton';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { useMutation } from '@tanstack/react-query';
import {
  deleteRecyclingCouponAPI,
  putRecyclingCouponAPI,
} from '@/src/apis/rest/recyclingCoupon';
import { toast } from '@/src/hooks/useToast';
import { TOAST_CONST } from '@/src/constants/toast';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import ImageUploadInfo from '@/components/molecule/ImageUploadInfo';
import React, { useRef } from 'react';
import { APIError } from '@/src/models/common/apiError';
import { OPTIONS_CONST } from '@/src/constants/options';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import { usePrompt } from '@/src/hooks/usePrompt';

interface RecyclingCouponDetailModalProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  onOk: () => void;
  data: RecyclingCouponDto;
}

const formSchema = () => {
  return z.object({
    sponsorName: z.string().min(1),
    onAt: z.string().min(1),
    offAt: z.string().min(1),
    couponMediaId: z.number(),
    isExposed: z.boolean(),
    exposeService: z.string(),
  });
};

export default function RecyclingCouponEditlModal({
  open: openProps,
  onOpenChange: onOpenChangeProps,
  onOk,
  data,
}: RecyclingCouponDetailModalProps) {
  // util
  const {
    Dialog: DeleteAlertDialog,
    isOpen: isDeleteAlertOpen,
    openDialog: openDeleteAlertDialog,
    closeDialog: closeDeleteAlertDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const {
    Dialog: BlockAlertDialog,
    isOpen: isBlockAlertOpen,
    openDialog: openBlockAlertDialog,
    closeDialog: closeBlockAlertDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const {
    Dialog: DuplicateErrorDialog,
    isOpen: isDuplicateErrorOpen,
    openDialog: openDuplicateErrorDialog,
    closeDialog: closeDuplicateErrorDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const {
    Dialog: EditDialog,
    isOpen: isEditOpen,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  // form
  const form = useForm<
    PutRecyclingCouponRequestBody & {
      postedAt: DateRange;
      couponMedia: Media;
    }
  >({
    resolver: zodResolver(formSchema()),
    defaultValues: {
      ...data,
      postedAt: {
        from: new Date(data.onAt),
        to: new Date(data.offAt),
      },
      couponMediaId: data.couponMedia.id,
    },
    reValidateMode: 'onSubmit',
  });
  const { field: couponMediaField } = useController({
    name: 'couponMedia',
    control: form.control,
  });
  const { field: onAtField } = useController({
    name: 'onAt',
    control: form.control,
  });
  const { field: offAtField } = useController({
    name: 'offAt',
    control: form.control,
  });

  // mutation
  const putRecyclingCouponMutation = useMutation({
    mutationFn: putRecyclingCouponAPI,
  });
  const deleteRecyclingCouponMutation = useMutation({
    mutationFn: deleteRecyclingCouponAPI,
  });

  // event
  const handleCloseModal = () => {
    if (form.formState.isDirty) {
      openBlockAlertDialog();
    } else {
      onOpenChangeProps(false);
    }
  };

  usePrompt(form.formState.isDirty && !form.formState.isSubmitted);

  const handleSubmit = (values: PutRecyclingCouponRequestBody) => {
    putRecyclingCouponMutation.mutate(
      { ...values, id: data.id },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          onOpenChangeProps(false);
          onOk();
        },
        onError: e => {
          const { error } = e as unknown as APIError;

          if (error === 'CANNOT_CREATE_RECYCLING_COUPON_IN_DUPLICATE_PERIOD') {
            openDuplicateErrorDialog();
          } else {
            toast({
              variant: 'error',
              title: TOAST_CONST.error.title,
              description: e.message,
            });
          }
        },
      },
    );
  };
  const handleBlockModalOk = () => {
    onOpenChangeProps(false);
  };
  const handleDeleteModalOk = () => {
    deleteRecyclingCouponMutation.mutate(
      { id: data.id },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.deleteSuccess.title,
            description: TOAST_CONST.deleteSuccess.description,
          });

          onOpenChangeProps(false);
          onOk();
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      },
    );
  };

  // render
  const getDateDisabledMatcher = (date: Date) => {
    if (dayjs().isAfter(date, 'D')) return true;

    return false;
  };
  const renderFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          className="flex-1"
          buttonColor="red"
          onClick={() => openDeleteAlertDialog()}
          isLoading={deleteRecyclingCouponMutation.isPending}
        >
          {BUTTON_CONST.delete}
        </Button>
        <Button
          key={String(form.formState.isValid)}
          type="button"
          className="flex-1"
          disabled={!form.formState.isDirty || !form.formState.isValid}
          onClick={openEditDialog}
          isLoading={putRecyclingCouponMutation.isPending}
        >
          {BUTTON_CONST.save}
        </Button>
      </div>
    );
  };

  return data ? (
    <>
      <Dialog
        open={openProps}
        onOpenChange={handleCloseModal}
        contentProps={{
          style: {
            width: 'calc(100vw - 24px)',
          },
          className: 'max-w-[697px]',
        }}
        title="배송비 쿠폰 수정"
        footer={renderFooter()}
      >
        <Form {...form}>
          <form
            ref={formRef}
            className="flex flex-col gap-6"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="sponsorName"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="후원사 명" isRequired error={error}>
                    <Input
                      containerClassName="flex-1"
                      onClear={() => field.onChange('')}
                      {...field}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="postedAt"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="게시 기간" isRequired error={error}>
                    <DateRangePicker
                      {...field}
                      onChange={e => {
                        field.onChange(e);

                        onAtField.onChange(
                          e?.from ? dayjs(e?.from).format('YYYY-MM-DD') : '',
                        );
                        offAtField.onChange(
                          e?.to ? dayjs(e?.to).format('YYYY-MM-DD') : '',
                        );
                      }}
                      dateDisabledMatcher={getDateDisabledMatcher}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="isExposed"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="노출 여부" error={error}>
                    <RadioGroup
                      optionList={[
                        { label: OPTIONS_CONST.exposed, value: 'true' },
                        { label: OPTIONS_CONST.notExposed, value: 'false' },
                      ]}
                      rootProps={{
                        value: field.value ? 'true' : 'false',
                        onValueChange: (e: string) =>
                          field.onChange(e === 'true'),
                      }}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="exposeService"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="노출 범위" error={error}>
                    <RadioGroup
                      optionList={[
                        { label: OPTIONS_CONST.onlyApp, value: 'APP' },
                        { label: OPTIONS_CONST.onlyWeb, value: 'WEB' },
                      ]}
                      rootProps={{
                        value: field.value,
                        onValueChange: field.onChange,
                      }}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="couponMediaId"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent
                    label="쿠폰 이미지"
                    isRequired
                    error={error}
                    defaultMessage={
                      <ImageUploadInfo widthSize={80} heightSize={80} />
                    }
                  >
                    <ImageUpload
                      width={128}
                      height={128}
                      widthText={80}
                      heightText={80}
                      maxImageSizeMb={5}
                      imageClassName="object-cover"
                      value={couponMediaField.value?.bufferFullUrl}
                      onChange={e => {
                        field.onChange(e.id);
                        couponMediaField.onChange(e);
                      }}
                      topAccessary={({ handleEdit, handleRemove }) => {
                        return (
                          <>
                            <div className="flex gap-x-1">
                              <IconButton
                                buttonSize="xSmall"
                                buttonColor="outline"
                                lucideIconProps={{ name: 'Pencil' }}
                                onClick={() => {
                                  handleEdit();
                                }}
                              />
                              <IconButton
                                buttonSize="xSmall"
                                buttonColor="outline"
                                lucideIconProps={{ name: 'X' }}
                                onClick={() => {
                                  handleRemove();
                                  field.onChange(undefined);
                                }}
                              />
                            </div>
                          </>
                        );
                      }}
                    />
                  </FormContent>
                );
              }}
            />
            <SectionLabel
              label="등록 일시"
              value={
                <Input
                  value={dayjs(data.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                  disabled
                  containerClassName="flex-1"
                />
              }
            />
            <SectionLabel
              label="등록 관리자"
              value={
                <Input
                  value={data?.administrator?.name}
                  disabled
                  containerClassName="flex-1"
                />
              }
            />
          </form>
        </Form>
        <BlockAlertDialog
          open={isBlockAlertOpen}
          onOpenChange={closeBlockAlertDialog}
          onCancel={() => {
            closeBlockAlertDialog();
          }}
          onOk={handleBlockModalOk}
          title="취소"
        >
          선택한 기간동안 앱에 노출 중인 배송비 쿠폰이 있습니다.
          <br />
          노출 중인 배송비 쿠폰을 삭제하거나 기간을 변경하여 다시 등록해주세요.
        </BlockAlertDialog>
        <DeleteAlertDialog
          open={isDeleteAlertOpen}
          onOpenChange={closeDeleteAlertDialog}
          onCancel={() => {
            closeDeleteAlertDialog();
          }}
          onOk={handleDeleteModalOk}
          title="배송비 쿠폰 삭제"
          isLoading={deleteRecyclingCouponMutation.isPending}
        >
          배송비 쿠폰을 삭제하시겠습니까?
        </DeleteAlertDialog>
        <DuplicateErrorDialog
          open={isDuplicateErrorOpen}
          onOpenChange={closeDuplicateErrorDialog}
          onCancel={() => {
            closeDuplicateErrorDialog();
          }}
          onOk={closeDuplicateErrorDialog}
          title="배송비 쿠폰 중복 불가"
          cancelButtonProps={{
            className: 'hidden',
          }}
        >
          <p className="whitespace-pre-wrap">
            선택한 기간동안 앱에 노출 중인 배송비 쿠폰이 있습니다.
            <br />
            노출 중인 배송비 쿠폰을 삭제하거나 기간을 변경하여 다시
            등록해주세요.
          </p>
        </DuplicateErrorDialog>
      </Dialog>

      {/* 모달 */}
      {isEditOpen && (
        <EditDialog
          open={isEditOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openEditDialog();
            } else {
              closeEditDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.edit}
          isLoading={putRecyclingCouponMutation.isPending}
        />
      )}
    </>
  ) : null;
}
